import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { TaskLabel } from '@this+that/models';

import { buildAbortableQueryContext } from './buildAbortableQueryContext';

const GET_TASK_LABELS = gql`
  query getTaskLabels($taskGroupId: String!) {
    getTaskLabels(taskGroupId: $taskGroupId) {
      taskGroupId
      id
      title
      createdFor
      createdAt
      updatedAt
      hexColor
    }
  }
`;

interface GetTaskLabelsParams {
  taskGroupId: string;
  signal?: AbortSignal;
}

interface GetTaskLabelsData {
  getTaskLabels: TaskLabel[];
}

interface CreateTaskLabelData {
  createTaskLabel: TaskLabel;
}

interface UpdateTaskLabelData {
  updateTaskLabel: TaskLabel;
}

interface DeleteTaskLabelData {
  deleteTaskLabel: boolean;
}

export function useGetTaskLabels() {
  const [runQuery, { loading }] = useLazyQuery<GetTaskLabelsData>(
    GET_TASK_LABELS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const getTaskLabels = useCallback(
    async ({ taskGroupId, signal }: GetTaskLabelsParams) => {
      const { data } = await runQuery({
        variables: { taskGroupId },
        context: buildAbortableQueryContext(signal),
      });

      return data?.getTaskLabels ?? [];
    },
    [runQuery],
  );

  return { getTaskLabels, loading };
}

const CREATE_TASK_LABEL = gql`
  mutation createTaskLabel($label: CreateTaskLabelInput!) {
    createTaskLabel(label: $label) {
      taskGroupId
      id
      title
      createdFor
      createdAt
      updatedAt
      hexColor
    }
  }
`;

export function useCreateTaskLabel() {
  const [createTaskLabelMutation, { loading, error }] =
    useMutation<CreateTaskLabelData>(CREATE_TASK_LABEL);

  const createTaskLabel = useCallback(
    async (label: Partial<TaskLabel>) => {
      const { data } = await createTaskLabelMutation({
        variables: { label },
      });

      return data?.createTaskLabel;
    },
    [createTaskLabelMutation],
  );

  return { createTaskLabel, loading, error };
}

const UPDATE_TASK_LABEL = gql`
  mutation updateTaskLabel($label: UpdateTaskLabelInput!) {
    updateTaskLabel(label: $label) {
      taskGroupId
      id
      title
      createdFor
      createdAt
      updatedAt
      hexColor
    }
  }
`;

export function useUpdateTaskLabel() {
  const [updateTaskLabelMutation, { loading, error }] =
    useMutation<UpdateTaskLabelData>(UPDATE_TASK_LABEL);

  const updateTaskLabel = useCallback(
    async (label: Partial<TaskLabel>) => {
      const { data } = await updateTaskLabelMutation({
        variables: { label },
      });

      return data?.updateTaskLabel;
    },
    [updateTaskLabelMutation],
  );

  return { updateTaskLabel, loading, error };
}

const DELETE_TASK_LABEL = gql`
  mutation deleteTaskLabel($taskGroupId: String!, $labelId: String!) {
    deleteTaskLabel(taskGroupId: $taskGroupId, labelId: $labelId)
  }
`;

export function useDeleteTaskLabel() {
  const [deleteTaskLabelMutation, { loading, error }] =
    useMutation<DeleteTaskLabelData>(DELETE_TASK_LABEL);

  const deleteTaskLabel = useCallback(
    async (taskGroupId: string, labelId: string) => {
      const { data } = await deleteTaskLabelMutation({
        variables: { taskGroupId, labelId },
      });

      return data?.deleteTaskLabel ?? false;
    },
    [deleteTaskLabelMutation],
  );

  return { deleteTaskLabel, loading, error };
}
